/* eslint-disable max-len */
import { useEffect, useState } from 'react';
import {
  useLocation,
  useNavigate,
} from 'react-router-dom';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Divider from '@material-ui/core/Divider';
import FormControl from '@material-ui/core/FormControl';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import { makeStyles } from '@material-ui/core/styles';
import CancelIcon from '@material-ui/icons/CancelOutlined';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CheckCircleOutlineRoundedIcon from '@material-ui/icons/CheckCircleOutlineRounded';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import PropTypes from 'prop-types';

import { ChangePasswordFirst, ConfirmationCodePassword } from '../../services/users';
import { setAnalytics } from '../../utils';
import TransitionsModal from '../General/TransitionsModal';

const useStyles = makeStyles((theme) => ({
  validations: {
    display: 'flex',
    width: '320px',
    marginLeft: theme.spacing(2),
    color: theme.palette.common.gray,
  },
  icon: {
    color: theme.palette.common.green,
    fontSize: 13,
    marginTop: theme.spacing(1),
  },
  iconError: {
    color: theme.palette.common.darkGray,
    fontSize: 13,
    marginTop: theme.spacing(1),
  },
  titleForm: {
    fontWeight: 'normal',
    fontSize: 16,
    color: theme.palette.common.blue,
    fontFamily: 'Raleway',
    marginTop: theme.spacing(2),
  },
  titleSession: {
    fontWeight: 600,
    fontSize: 24,
    color: theme.palette.common.blue,
    fontFamily: 'Raleway',
    [theme.breakpoints.between('lg', 'xl')]: {
      marginTop: theme.spacing(3),
    },
    [theme.breakpoints.up('xl')]: {
      marginTop: theme.spacing(5),
    },
  },
  sectionDivider: {
    background: theme.palette.common.pink,
    size: '5',
    margin: theme.spacing(1, 1, 1),
    borderBottomWidth: 4,
  },
  input: {
    border: `1px solid ${theme.palette.background.primary}`,
    borderRadius: 'px',
    background: theme.palette.background.default,
    [theme.breakpoints.down('md')]: {
      width: '210px',
    },
    [theme.breakpoints.up('md')]: {
      width: '300px',
    },
    marginTop: theme.spacing(1),
  },
  error: {
    color: theme.palette.common.red,
    margin: '5px',
  },
  forget: {
    fontWeight: 'normal',
    fontSize: 14,
    color: theme.palette.text.alert,
    fontFamily: 'Raleway',
    marginTop: theme.spacing(2),
  },
  textList: {
    fontWeight: '600',
    fontSize: 11,
    color: theme.palette.common.green,
    fontFamily: 'Raleway',
    marginTop: theme.spacing(1),
  },
  textListError: {
    fontWeight: '600',
    fontSize: 11,
    color: theme.palette.common.darkGray,
    fontFamily: 'Raleway',
    marginTop: theme.spacing(1),
  },
  politics: {
    marginTop: theme.spacing(3),
    padding: '4px',
  },
  textPolitics: {
    fontWeight: '400',
    fontSize: 13,
    color: theme.palette.common.blue,
    fontFamily: 'Raleway',
    marginTop: theme.spacing(2),
  },
  submit: {
    color: theme.palette.background.default,
    boxShadow: theme.shadows[1],
    fontWeight: '400',
    textTransform: 'capitalize',
    background: theme.palette.background.secondary,
    fontSize: 16,
    fontFamily: 'Raleway',
    '&:hover': {
      background: theme.palette.common.pinkLigth,
    },
    '&:disabled': {
      background: theme.palette.common.gray,
    },
    [theme.breakpoints.between('lg', 'xl')]: {
      marginTop: theme.spacing(2),
      padding: '6px 30px',
    },
    [theme.breakpoints.up('xl')]: {
      marginTop: theme.spacing(8),
      padding: '6px 30px',
    },
  },
  buttonProgress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
}));

function PasswordFirstTime({ sessionLogin, setIsFirstLoging, userName }) {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const [openModalConfirmation, setOpenModalConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [codeInvalid, setCodeInvalid] = useState(false);

  const [minChar, setMinChar] = useState(false);
  const [lowerandUpper, setLowerAndUpper] = useState(false);
  const [numberChar, setNumberChar] = useState(false);
  const [specialChar, setSpecialChar] = useState(false);

  const [form, setForm] = useState({
    newPassword: '',
    confirmationPassword: '',
    showPassword: false,
  });

  const handleValid = (values) => {
    if (values.newPassword.length > 8 || values.newPassword.length === 8) {
      setMinChar(true);
    } else {
      setMinChar(false);
    }
    if (values.newPassword.match(/.*[0-9].*/)) {
      setNumberChar(true);
    } else {
      setNumberChar(false);
    }
    if (values.newPassword.match(/^(?=.*[A-Z])(?=.*[a-z]).*$/)) {
      setLowerAndUpper(true);
    } else {
      setLowerAndUpper(false);
    }
    if (values.newPassword.match(/[$&+,:;=?@#|'<>.^*()%!-]/)) {
      setSpecialChar(true);
    } else {
      setSpecialChar(false);
    }
  };

  const handleChangePassword = (event) => {
    const values = { ...form, [event.target.name]: event.target.value };
    setForm(values);
    handleValid(values);
  };

  const handleClickShowPassword = () => {
    setForm({ ...form, showPassword: !form.showPassword });
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleCloseModalCodeInvalid = () => {
    setCodeInvalid(true);
    setIsFirstLoging(false);
    navigate('/', {
      state: null,
    });
  };

  const passwordChangeConfirmation = async () => {
    if (location.state) {
      setLoading(true);
      try {
        setAnalytics('Login', 'Modal_Exito_CambioDeContraseña');
        await ConfirmationCodePassword(
          location.state.name,
          location.state.setCode,
          form.newPassword,
        );
        setLoading(false);
        setOpenModalConfirmation(true);
      } catch (err) {
        if (err.message === 'Invalid verification code provided, please try again.') {
          setLoading(false);
          setCodeInvalid(true);
        }
      }
    } else {
      await ChangePasswordFirst(
        userName,
        form.newPassword,
        sessionLogin,
      );
      setOpenModalConfirmation(true);
    }
  };
  const closeModalConfirmation = () => {
    setOpenModalConfirmation(false);
    setIsFirstLoging(false);
    navigate('/', {
      state: null,
    });
  };
  const styleIconModal = (e) => (<CheckCircleOutlineRoundedIcon className={e} />);
  const styleIconModalValidCode = (e) => <ErrorOutlineIcon className={e} />;

  useEffect(() => {
  }, []);

  return (
    <>
      <Grid
        container
        direction="column"
        justifyContent="center"
        alignItems="center"
      >
        <span className={classes.titleSession}>Nueva contraseña</span>
      </Grid>
      <Divider className={classes.sectionDivider} variant="middle" />
      <Grid
        container
        direction="column"
        justifyContent="center"
        alignItems="center"
      >
        <FormControl>
          <span className={classes.titleForm}>Nueva contraseña:</span>
          <OutlinedInput
            className={classes.input}
            id="filled-adornment-password"
            type={form.showPassword ? 'text' : 'password'}
            name="newPassword"
            margin="dense"
            value={form.newPassword}
            onChange={(event) => {
              handleChangePassword(event);
            }}
            endAdornment={(
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                >
                  {form.showPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            )}
          />
        </FormControl>
        <FormControl>
          {' '}
          <Grid
            className={classes.validations}
            xs={12}
            container
            direction="row"
            wrap="no-wrap"
          >
            {' '}
            <Grid xs={1} direction="column" container>
              {minChar ? (
                <CheckCircleIcon className={classes.icon} />
              ) : (
                <CancelIcon className={classes.iconError} />
              )}
              {lowerandUpper ? (
                <CheckCircleIcon className={classes.icon} />
              ) : (
                <CancelIcon className={classes.iconError} />
              )}
              {numberChar ? (
                <CheckCircleIcon className={classes.icon} />
              ) : (
                <CancelIcon className={classes.iconError} />
              )}
              {specialChar ? (
                <CheckCircleIcon className={classes.icon} />
              ) : (
                <CancelIcon className={classes.iconError} />
              )}
              {form.newPassword === form.confirmationPassword && form.newPassword !== '' ? (
                <CheckCircleIcon className={classes.icon} />
              ) : (
                <CancelIcon className={classes.iconError} />
              )}
            </Grid>
            <Grid xs={11} direction="column" container>
              {' '}
              <span
                className={
                  minChar ? classes.textList : classes.textListError
                }
              >
                Mínimo 8 caracteres
              </span>
              <span
                className={
                  lowerandUpper ? classes.textList : classes.textListError
                }
              >
                Al menos una mayúscula y una minúscula
              </span>
              <span
                className={
                  numberChar ? classes.textList : classes.textListError
                }
              >
                Al menos un caracter numérico
              </span>
              <span
                className={
                  specialChar ? classes.textList : classes.textListError
                }
              >
                Al menos un caracter especial
              </span>
              <span
                className={
                  form.newPassword === form.confirmationPassword && form.newPassword !== ''
                    ? classes.textList
                    : classes.textListError
                }
              >
                Las contraseñas coinciden
              </span>
            </Grid>
          </Grid>
        </FormControl>
        <FormControl>
          <span className={classes.titleForm}>Confirmación de contraseña:</span>
          <OutlinedInput
            className={classes.input}
            id="filled-adornment-password"
            type={form.showPassword ? 'text' : 'password'}
            name="confirmationPassword"
            margin="dense"
            value={form.confirmationPassword}
            onChange={(event) => {
              handleChangePassword(event);
            }}
            endAdornment={(
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                >
                  {form.showPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            )}
          />
        </FormControl>
        <FormControl>
          <Button
            type="submit"
            size="large"
            className={classes.submit}
            onClick={passwordChangeConfirmation}
            open={openModalConfirmation}
            onClose={closeModalConfirmation}
            disabled={!((form.newPassword && form.confirmationPassword) && (form.newPassword === form.confirmationPassword))}
          >
            CONFIRMAR
            {loading && (
              <CircularProgress size={24} className={classes.buttonProgress} />
            )}
          </Button>
        </FormControl>
      </Grid>
      <TransitionsModal
        open={openModalConfirmation}
        handleClose={closeModalConfirmation}
        textBody="¡Tu contraseña se cambió con éxito!"
        icon={styleIconModal}
      />
      <TransitionsModal
        open={codeInvalid}
        handleClose={handleCloseModalCodeInvalid}
        textBody="El código registrado no es correcto,
      vuelve a solicitar un nuevo código en
      “He olvidado mi contraseña”"
        icon={styleIconModalValidCode}
      />
    </>
  );
}

PasswordFirstTime.propTypes = {
  sessionLogin: PropTypes.string.isRequired,
  setIsFirstLoging: PropTypes.string.isRequired,
  userName: PropTypes.string.isRequired,
};
export default PasswordFirstTime;
